"use client";
import React, { CSSProperties, useLayoutEffect, useRef, useState } from "react";
import Link from "next/link";
import { Check, Xmark, NavArrowDown } from "iconoir-react";
import classNames from "classnames";
import {
  Card,
  Dialog,
  DialogCloseButton,
  Typography,
} from "@cappelendamm/design-system/react";
import { getYear } from "date-fns";
import { NormalizedProductVariant } from "@cappelendamm/catalogue/crystallize/product-translator.js";
import { PricelistPrice } from "./PricelistPrice";

function formatVariantInfo({
  language,
  publicationDate,
}: {
  language?: string;
  publicationDate?: string;
}) {
  return [language, publicationDate ? getYear(publicationDate) : null]
    .filter(Boolean)
    .join(", ");
}

export const VariantSelectorProductPage = ({
  variants,
  currentSku,
}: {
  currentSku: string;
  variants: NormalizedProductVariant[];
}) => {
  const [pos, setPos] = useState<number | undefined>();
  const dialog = useRef<HTMLDialogElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const currentBook = variants.find((variant) => variant.sku === currentSku);
  if (currentBook?.itemType !== "book") return null;

  const onResize = () => {
    const triggerTop = trigger.current?.getBoundingClientRect().top;
    const dialogHeight = dialog.current?.getBoundingClientRect().height;
    const windowHeight = window.innerHeight;

    if (
      triggerTop &&
      dialogHeight &&
      windowHeight - (triggerTop + dialogHeight) > triggerTop
    )
      setPos(triggerTop);
    else if (dialogHeight) setPos((windowHeight - dialogHeight) / 2);
  };

  useLayoutEffect(() => {
    window.addEventListener("resize", onResize);
    onResize();
    return () => {
      window.removeEventListener("resize", onResize);
    };
  }, []);

  return (
    <div
      className="relative w-full [&:has(:popover-open)>button_svg]:rotate-180"
      style={
        {
          "--top": pos ? `${pos}px` : undefined,
        } as CSSProperties
      }
    >
      <button
        ref={trigger}
        onClick={() => {
          if (dialog.current) {
            dialog.current?.showModal();
            onResize();
          }
        }}
        className="bg-whi flex w-full items-center gap-2 rounded border bg-white p-4 text-left focus:[box-shadow:var(--cvr-focus)]"
      >
        <Typography
          as="span"
          variant="title-md"
          className="text-nowrap first-letter:capitalize"
        >
          {currentBook?.itemDetails.binding}
        </Typography>
        <Typography
          as="span"
          variant="body-lg"
          className="grow justify-self-start text-nowrap"
        >
          {formatVariantInfo({
            language: currentBook?.itemDetails.language,
            publicationDate: currentBook?.itemDetails.publicationDate,
          })}
        </Typography>
        <NavArrowDown width={24} height={24} className="transition" />
      </button>
      <Dialog
        ref={dialog}
        className="grid-cols-[1fr_auto] content-start gap-4 open:grid sm:inset-0 sm:m-auto sm:min-w-[var(--cvr-prose-width)] sm:content-normal"
      >
        <Typography as="h2" variant="title-lg" className="self-center">
          {currentBook?.name}
        </Typography>
        <DialogCloseButton
          onClick={() => {
            if (dialog.current) dialog.current.close();
          }}
        >
          <Xmark width={24} height={24} />
        </DialogCloseButton>
        <ul className="col-span-full mt-3 grid grid-cols-[auto_1fr_auto] content-start gap-x-4 gap-y-2 sm:mt-4 sm:content-normal sm:gap-4">
          {variants
            .filter((variant) => variant.itemType === "book")
            .filter((variant) => !!variant.itemDetails.slug)
            .map((variant) => (
              <li
                key={variant.sku}
                className="col-span-full grid grid-cols-subgrid"
              >
                <Card
                  as={Link}
                  className={classNames(
                    "col-span-full grid h-fit max-h-fit grid-cols-subgrid grid-rows-2 items-center gap-y-1 p-4 hover:bg-[#EBEDF7] active:bg-[#EBEDF7] active:cvr-focus",
                    {
                      "bg-[#EBEDF7]": variant.sku === currentSku,
                    },
                  )}
                  href={`/boker/${variant.itemDetails.slug}`}
                >
                  {variant.sku === currentSku ? (
                    <Check width={24} height={24} className="row-span-2" />
                  ) : (
                    <span className="row-span-2" />
                  )}
                  <Typography
                    variant="title-md"
                    as="span"
                    className="no-wrap col-start-2 first-letter:capitalize"
                  >
                    {variant.itemDetails.binding}
                  </Typography>
                  <Typography
                    className="col-start-2 row-start-2 text-nowrap"
                    as="span"
                  >
                    {formatVariantInfo({
                      language: variant.itemDetails.language,
                      publicationDate: variant.itemDetails.publicationDate,
                    })}
                  </Typography>
                  <PricelistPrice
                    originalItemPrice={variant.price}
                    subjectToVAT={variant.subjectToVAT}
                    pricelists={variant.priceLists ?? []}
                  />
                </Card>
              </li>
            ))}
        </ul>
      </Dialog>
    </div>
  );
};
